import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiGet } from '../lib/apiClient';

const BrandingContext = createContext({
  branding: { accent_color: '#0B3D91', display_name: '', logo_url: '', sidebar_style: 'default' },
  loading: true,
  refresh: () => {},
});

const hexToHSL = (hex) => {
  let r = 0, g = 0, b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16); g = parseInt(hex[2] + hex[2], 16); b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16); g = parseInt(hex.slice(3, 5), 16); b = parseInt(hex.slice(5, 7), 16);
  }
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
};

const applyBrandingToDOM = (branding) => {
  const root = document.documentElement;
  const color = branding.accent_color || '#0B3D91';
  root.style.setProperty('--brand-accent', color);

  const hsl = hexToHSL(color);
  root.style.setProperty('--brand-accent-h', hsl.h);
  root.style.setProperty('--brand-accent-s', `${hsl.s}%`);
  root.style.setProperty('--brand-accent-l', `${hsl.l}%`);

  // Lighter and darker variants
  root.style.setProperty('--brand-accent-light', `hsl(${hsl.h}, ${hsl.s}%, ${Math.min(hsl.l + 30, 95)}%)`);
  root.style.setProperty('--brand-accent-dark', `hsl(${hsl.h}, ${hsl.s}%, ${Math.max(hsl.l - 15, 10)}%)`);
  root.style.setProperty('--brand-accent-muted', `hsl(${hsl.h}, ${Math.max(hsl.s - 30, 10)}%, ${Math.min(hsl.l + 40, 96)}%)`);
};

export const BrandingProvider = ({ children, token }) => {
  const [branding, setBranding] = useState({
    accent_color: '#0B3D91', display_name: '', logo_url: '', sidebar_style: 'default',
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const data = await apiGet('/api/org/branding');
      if (data) {
        setBranding(data);
        applyBrandingToDOM(data);
      }
    } catch (e) {
      console.error('Failed to load branding:', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <BrandingContext.Provider value={{ branding, loading, refresh }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => useContext(BrandingContext);
